import { NgFor, NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { fileSizeValidator } from '@common/file-size-validator';
import { fileTypeValidator } from '@common/file-type-validator';
import { FormHelper } from '@common/form-helper';
import { ConsultationRequestHeaderComponent } from '@components/consultation-request-header/consultation-request-header.component';
import { FileInputComponent } from '@components/dynamic-forms/file-input/file-input.component';
import { ProcessingWarningNotification } from '@components/processing-warning-notification/processing-warning-notification';
import { ConsultationRequestAttachmentType } from '@enums/consultation-request-attachment-type';
import { InputTypes } from '@enums/input-types';
import { ValidatorFunctions } from '@enums/validator-functions';
import { ConsultationRequestOrderDetail } from '@models/consultation-request/consultation-request-order-detail';
import { Question } from '@models/dynamic-forms/question';
import { QuestionValidator } from '@models/dynamic-forms/question-validator';
import { IndexedDbItem } from '@models/indexed-db-item';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { IndexedDbService } from '@services/indexed-db.service';
import { NavigationService } from '@services/navigation.service';
import { TreatmentConsultationSubmissionService } from '@services/treatment-consultation-submission.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-consult-attachment',
  templateUrl: './consult-attachment.component.html',
  styleUrls: ['./consult-attachment.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [ConsultationRequestHeaderComponent, NgIf, FileInputComponent, NgFor, ProcessingWarningNotification],
})
export class ConsultAttachmentComponent extends FormHelper implements OnInit, OnDestroy {
  public form: FormGroup;
  public question: Question;
  public isSubmitting: boolean = false;
  public isDisabled: boolean = true;

  private readonly controlName = 'governmentIdFile';
  private readonly attachmentValidators: QuestionValidator[] = [
    {
      validatorFn: { name: ValidatorFunctions.Required },
      errorMessage: 'Please upload a file',
    },
    {
      validatorFn: {
        name: ValidatorFunctions.FileType,
        params: [['image/jpeg', 'image/png', 'image/gif', 'image/heic', 'application/pdf']],
      },
      errorMessage: 'Invalid file type, please upload a JPEG, PNG, GIF, HEIC, or PDF file',
    },
    {
      validatorFn: { name: ValidatorFunctions.MaxFileSize, params: [10485760] },
      errorMessage: 'File size should not exceed 10MB',
      errorKey: 'fileSize',
    },
  ];
  private readonly unauthorizedErrorMessage = 'This action is unauthorized.';
  private readonly submissionErrorMessage =
    'Sorry, there was a problem uploading the ID attachment. Please select another file or try again.';

  private submissionError: string | null = null;
  private subscriptions: Subscription[] = [];

  private indexedDbService = inject(IndexedDbService);
  private consultationStorageService = inject(ConsultationStorageService);
  private navigationService = inject(NavigationService);
  private treatmentConsultationSubmissionService = inject(TreatmentConsultationSubmissionService);
  private errorLoggingService = inject(ErrorLoggingService);
  private consultationRequestService = inject(ConsultationRequestService);

  /**
   * Retrieves error messages for the file input.
   */
  public get errors(): string[] {
    if (this.submissionError) {
      return [this.submissionError];
    }

    if (!this.isInvalid(this.control)) {
      return [];
    }

    return Object.keys(this.control.errors || {}).map(
      (key) => this.attachmentValidators.find((v) => v.errorKey === key || v.validatorFn.name === key).errorMessage,
    );
  }

  /**
   * Gets the form control for the file input.
   */
  private get control(): FormControl {
    return this.form.get(this.controlName) as FormControl;
  }

  /**
   * Gets the name of the IndexedDB database used for storing attachments.
   */
  private get indexedDbName(): string {
    return `${this.consultationStorageService.transactionId}-consultation-attachments`;
  }

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.question = this.getQuestion();
    this.form = this.getFormGroup();
    this.setAttachmentFromIndexedDb();
  }

  /**
   * Unsubscribes from all current subscriptions.
   */
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Uploads an ID attachment file and navigates to the next page of the consultation request process.
   */
  public submit(): void {
    this.submissionError = null;
    this.isSubmitting = true;

    if (this.consultationStorageService.idAttachmentUploaded) {
      this.navigationService.navigateToNextConsultationRequestPage(this.consultationStorageService.treatmentType);

      return;
    }

    this.treatmentConsultationSubmissionService
      .createAttachmentObservable({
        file: this.control.value,
        type: ConsultationRequestAttachmentType.Id,
      })
      .subscribe({
        next: () => {
          this.consultationStorageService.idAttachmentUploaded = true;
          this.navigationService.navigateToNextConsultationRequestPage(this.consultationStorageService.treatmentType);
        },
        error: (err) => this.handleErrorResponse(err),
      });
  }

  /**
   * Creates a form group with validation for the file input.
   *
   * @returns {FormGroup} a form group with required, file type and size validators
   */
  private getFormGroup(): FormGroup {
    return new FormGroup({
      [this.controlName]: new FormControl(null, this.buildValidators()),
    });
  }

  /**
   * Generates a question object for the file input.
   *
   * @returns {Question} a Question object
   */
  private getQuestion(): Question {
    return new Question({
      id: this.controlName,
      type: InputTypes.File,
      label: 'To verify your identity, please upload a photo of your government identification card (ID).',
      validators: this.attachmentValidators,
    });
  }

  /**
   * Builds an array of validator functions from a question's validator configuration.
   *
   * @returns {ValidatorFn[]} an array of validator functions
   */
  private buildValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    this.attachmentValidators.forEach((validator) => {
      switch (validator.validatorFn.name) {
        case ValidatorFunctions.Required:
          validators.push(Validators.required);
          break;
        case ValidatorFunctions.FileType:
          validators.push(fileTypeValidator(validator.validatorFn.params[0]));
          break;
        case ValidatorFunctions.MaxFileSize:
          validators.push(fileSizeValidator(validator.validatorFn.params[0]));
          break;
      }
    });
    return validators;
  }

  /**
   * Subscribes to file changes in the form control and manages IndexedDB storage accordingly.
   */
  private subscribeToFileChanges(): void {
    this.subscriptions.push(
      this.control.valueChanges.subscribe((file: File) => {
        this.submissionError = null;

        if (!file) {
          this.indexedDbService.deleteItem(this.indexedDbName, this.controlName).subscribe();

          return;
        }

        this.indexedDbService.saveItem(this.indexedDbName, file, this.controlName).subscribe();
      }),
    );
  }

  /**
   * Retrieves and restores attachment data from IndexedDB to the form controls.
   */
  private setAttachmentFromIndexedDb(): void {
    this.indexedDbService.getAllItems(this.indexedDbName).subscribe((answers) => {
      const governmentIdItem = answers.find((answer: IndexedDbItem<any>) => answer.key === this.controlName);
      const control = this.form.get(this.controlName);

      if (governmentIdItem?.data && control) {
        control.patchValue(governmentIdItem.data);
        control.updateValueAndValidity();
        control.markAsDirty();
      }

      this.subscribeToFileChanges();
      this.isDisabled = false;
    });
  }

  /**
   * Handles error responses from the attachment submission.
   *
   * @param {any} error The error response object.
   */
  private handleErrorResponse(error: any): void {
    if (error?.status === 403 && error.error.message === this.unauthorizedErrorMessage) {
      this.refreshToken();

      return;
    }

    this.setSubmissionErrorState(error);
  }

  /**
   * Sets the submission error state and logs the error.
   *
   * @param {any} error The error object to log.
   */
  private setSubmissionErrorState(error?: any): void {
    this.submissionError = this.submissionErrorMessage;
    this.isSubmitting = false;

    if (error) {
      this.errorLoggingService.logError(error);
    }
  }

  /**
   * Refreshes the authentication token
   */
  private refreshToken(): void {
    this.consultationRequestService
      .getConsultationOrderDetails(
        this.consultationStorageService.transactionId,
        this.consultationStorageService.hash,
        this.consultationStorageService.treatmentType,
        { [this.consultationRequestService.patRequestHeaderName]: 'true' },
      )
      .subscribe({
        next: (orderDetail: ConsultationRequestOrderDetail) => {
          this.consultationStorageService.createAttachmentToken = orderDetail.anonymousToken || '';
          this.setSubmissionErrorState();
        },
        error: (err) => this.setSubmissionErrorState(err),
      });
  }
}
